import { useEffect, useState } from 'react'
import {
  RECOMMENDED_ACTIVITIES_CATEGORIES,
  type RecommendedActivitiesCategory,
} from '@domain/features/suggested-activities/suggested-activities-categories'
import {
  selectRecipeForToday,
  selectSuggestedActivityForTodayByCategoryIds,
} from '@domain/features/suggested-activities/suggested-activities-feature'
import type { Recipe, SuggestedActivity } from '@domain/models/suggested-activities'

export type RecommendedActivityContent = SuggestedActivity | Recipe | null

export interface RecommendedActivityTile {
  category: RecommendedActivitiesCategory
  content: RecommendedActivityContent
}

export function useRecommendedActivities() {
  const [tiles, setTiles] = useState<RecommendedActivityTile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)

        const results = await Promise.all(
          RECOMMENDED_ACTIVITIES_CATEGORIES.map(async (category) => {
            if (category.source === 'recipe') {
              const recipe = await selectRecipeForToday()
              return { category, content: recipe }
            }

            const suggestedActivity =
              await selectSuggestedActivityForTodayByCategoryIds(category.categoryIds)
            return { category, content: suggestedActivity }
          }),
        )

        if (!cancelled) {
          setTiles(results)
        }
      } catch (cause) {
        if (!cancelled) {
          setError(
            cause instanceof Error
              ? cause.message
              : 'Unable to load recommended activities.',
          )
          setTiles(
            RECOMMENDED_ACTIVITIES_CATEGORIES.map((category) => ({
              category,
              content: null,
            })),
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return { tiles, loading, error }
}
